import type {
  documentNode,
  elementNode,
  event,
  fullSnapshotEvent,
  incrementalSnapshotEvent,
  metaEvent,
  mutationData,
  serializedNode,
} from '@rrweb/types'
import { EventType, NodeType } from '@rrweb/types'
import { IncrementalSource } from 'rrweb'

import type { RrwebModule, RrwebPackerModule } from '@core/rrweb/types'

/**
 * Lazy loads rrweb package.
 * @return rrweb module.
 */
export async function loadRrweb(): Promise<RrwebModule> {
  return await import('rrweb')
}

/**
 * Lazy loads rrweb packer package.
 * @returns rrweb packer module.
 */
export async function loadRrwebPacker(): Promise<RrwebPackerModule> {
  return await import('@rrweb/packer')
}

/**
 * @returns Whether an rrweb event is a meta event.
 */
export function isMetaEvent(event: event): event is metaEvent {
  return event.type === EventType.Meta
}

/**
 * @returns Whether an rrweb event is a full snapshot.
 */
export function isFullSnapshotEvent(event: event): event is fullSnapshotEvent {
  return event.type === EventType.FullSnapshot
}

/**
 * @returns Whether an rrweb event is an incremental event.
 */
export function isIncrementalSnapshotEvent(
  event: event,
): event is incrementalSnapshotEvent {
  return event.type === EventType.IncrementalSnapshot
}

/**
 * @returns Whether an rrweb event data is a mutation data.
 */
export function isMutationData(
  data: incrementalSnapshotEvent['data'],
): data is mutationData {
  return data.source === IncrementalSource.Mutation
}

/**
 * @returns Whether a mutated node is an element node.
 */
export function isElementNode(node: serializedNode): node is elementNode {
  return node.type === NodeType.Element
}

/**
 * @returns Whether a node is a document node.
 */
export function isDocumentNode(node: serializedNode): node is documentNode {
  return node.type === NodeType.Document
}

/**
 * Recursively updates the attributes of a node so it becomes non-interactable.
 *
 * @returns The rrweb node.
 */
export function makeNodeNonInteractable<T extends serializedNode>(node: T): T {
  if (isDocumentNode(node) || isElementNode(node)) {
    for (const childNode of node.childNodes) {
      makeNodeNonInteractable(childNode)
    }
  }

  if (isElementNode(node)) {
    node.attributes.readonly = true
    node.attributes.tabIndex = -1

    // Prevent video playback to save bandwidth in replayer
    if (node.attributes.src && ['video', 'source'].includes(node.tagName)) {
      node.attributes.src = ''
    }
  }

  return node
}
